// Shared vocabulary for the stream ingest page: the four transport lags and
// the four reasons a span never became durable, in the order the endpoint
// lists them, each with the reading it carries. Source of truth is
// `api/services/stream/metrics/latency.py` in reclip-api — keys here are its keys.
//
// Every chart, legend and table column on the page speaks this vocabulary so
// a color means the same thing wherever it appears.

// Keys name the interval start to end, so `end_to_sealed` is
// `sealed_at - end_ms`: audio ended, then bytes were verified.
export const LAGS = [
  {
    key: 'end_to_sealed',
    label: 'End → durable',
    color: '#4e79a7',
    help: 'From the end of recording to the audio being safe here. The two below split it.',
  },
  {
    key: 'end_to_declared',
    label: 'End → declared',
    color: '#e15759',
    help: 'From the end of recording to the phone telling us. Long means the phone was asleep or offline, or waiting its turn: reclip-08 declares a span only when it can start uploading it, and on cellular holds short ones back up to 30 min.',
  },
  {
    key: 'declared_to_sealed',
    label: 'Declared → durable',
    color: '#f28e2c',
    help: 'From the phone telling us to every segment arriving: the upload.',
  },
  {
    key: 'sealed_to_routed',
    label: 'Durable → routed',
    color: '#b07aa1',
    help: 'From safe to the router finishing the span.',
  },
  {
    key: 'sealed_to_processed',
    label: 'Durable → processed',
    color: '#76b7b2',
    help: 'From safe to transcribed.',
  },
]

// The product half (audio-streaming step 4a), read off `product.days` of the
// latency endpoint: over moments the pipeline made, never moments it missed —
// there is no denominator over conversations nobody found (invariant 11). The
// two push lags (step 4b) cover only quotes that were pushed: to phones running
// the merged app (step 6a), and none on develop, where push is off. The review's lag (step 5)
// covers only quotes their owner kept.
export const PRODUCT_LAGS = [
  {
    key: 'moment_to_surfaced',
    label: 'Moment → surfaced',
    color: '#59a14f',
    help: 'From the end of a moment to its quote being ready: all of the server’s work together.',
  },
  {
    key: 'transcribed_to_selected',
    label: 'Transcribed → selected',
    color: '#edc948',
    help: 'How long picking quotes takes once a span is transcribed.',
  },
  {
    key: 'moment_to_push',
    label: 'Moment → push',
    color: '#4e79a7',
    help: 'From the end of a moment to the push about it: the number the product is judged by. Only pushed quotes count.',
  },
  {
    key: 'push_to_fetch',
    label: 'Push → fetched',
    color: '#9c755f',
    help: 'From the push to the phone first loading the quote. Long means the push didn’t reach the phone, or wasn’t opened.',
  },
  {
    key: 'surfaced_to_released',
    label: 'Surfaced → kept',
    color: '#d37295',
    help: 'How long a quote waits in its owner’s tray before they keep it. Only kept quotes count, so recent days read low.',
  },
]

// Percentiles are nearest-rank over that day's sample, so on a small sample
// p90 sits at or near the worst span: read the count beside it, never the
// percentile alone.
export const PERCENTILES = [
  { key: 'p50_s', label: 'p50' },
  { key: 'p90_s', label: 'p90' },
  { key: 'max_s', label: 'max' },
]

// How a span that never reached durable got there. Worst first, and
// exhaustive: the four always sum to never_durable, which is what lets the
// chart's total be read as the loss count itself.
export const REASONS = [
  {
    key: 'nothing_received',
    label: 'Nothing received',
    color: '#e15759',
    help: 'Nothing arrived within {horizon}. A late upload can still save it.',
  },
  {
    key: 'partial_upload',
    label: 'Partial upload',
    color: '#f28e2c',
    help: 'Only part arrived within {horizon}; we keep what came. A late upload can still finish it.',
  },
  {
    key: 'stuck_declared',
    label: 'Stuck declared',
    color: '#af7aa1',
    help: 'Past {horizon} and not written off yet: the next sweep will.',
  },
  {
    key: 'in_flight',
    label: 'In flight',
    color: '#8a8f98',
    help: 'Declared within {horizon} and still arriving. Not lost.',
  },
]

// Outcome bands of the durability chart, bottom of the stack first. Totals
// to `declared`, so the bar's height is the day's volume and its color is
// the day's health.
export const OUTCOMES = [
  {
    key: 'durable_by_client',
    label: 'Durable',
    color: '#59a14f',
    help: 'Arrived in full, and the phone confirmed it.',
  },
  {
    key: 'durable_by_sweep',
    label: 'Durable (sweep)',
    color: '#76b7b2',
    help: 'Arrived in full, but the phone never confirmed it, so the sweep did. Left out of the times.',
  },
  {
    key: 'never_durable',
    label: 'Never durable',
    color: '#e15759',
    help: 'Never arrived in full. Broken out by why below.',
  },
]

// Seal causes are an open vocabulary — a client may add one without a server
// release — so a chart of them cannot ship a fixed series list. Known causes
// get a color that carries their meaning: the ordinary ways a span closes
// read calm, the ways that mean something went wrong read hot. Anything
// unrecognised takes the next spare color, stably by name, so a cause this
// page has never heard of still charts.
export const CAUSE_COLORS = {
  span_limit: '#4e79a7',
  user_stop: '#59a14f',
  clip_playback: '#76b7b2',
  interruption: '#f28e2c',
  permission_revoked: '#b07aa1',
  system_kill: '#e15759',
  force_quit: '#d37295',
  crash: '#a0453f',
  jetsam: '#ff9d9a',
}

//: What each known cause means on the device. An unknown cause still charts
//: and still gets a tooltip — just a generic one, since only the client knows.
export const CAUSE_HELP = {
  span_limit:
    'The span hit its length limit and the device rolled to a new one. The ordinary close.',
  user_stop: 'The person stopped recording.',
  clip_playback:
    'A quote played for over 2 minutes, so the span closed where it started. On reclip-08 a shorter one leaves a gap inside the span instead.',
  other_audio:
    'Another app’s audio played for over 2 minutes, so the span closed where it started. On reclip-08 a shorter one leaves a gap inside the span instead.',
  interruption:
    'Something took the mic mid-recording — typically a phone call.',
  stalled: 'The mic stopped delivering audio and iOS gave no reason.',
  user_pause: 'The person paused recording for a set time.',
  permission_revoked: 'Mic permission was withdrawn while recording.',
  system_kill: 'iOS terminated the app.',
  force_quit: 'The person swiped the app away.',
  logout:
    'The person logged out. The phone deletes its recordings as it does, so a span rarely reaches the server with this.',
  crash: 'The app crashed.',
  jetsam: 'iOS killed the app under memory pressure.',
  device_restart: 'The phone restarted, or iOS updated, while recording.',
  app_upgrade: 'The app was updated while recording.',
}

const SPARE_COLORS = ['#9c755f', '#bab0ac', '#8cd17d', '#b6992d', '#86bcb6']

//: Spans that carry no cause at all — everything recorded before the field
//: shipped. Grey, and never mistakable for a real cause.
export const NO_CAUSE_COLOR = '#8a8f98'
export const NO_CAUSE_LABEL = 'not reported'

export const causeLabel = (value) =>
  value == null ? NO_CAUSE_LABEL : value.replace(/_/g, ' ')

export const causeColor = (value) => {
  if (value == null) return NO_CAUSE_COLOR
  if (CAUSE_COLORS[value]) return CAUSE_COLORS[value]
  let hash = 0
  for (let i = 0; i < value.length; i += 1)
    hash = (hash * 31 + value.charCodeAt(i)) | 0
  return SPARE_COLORS[Math.abs(hash) % SPARE_COLORS.length]
}

// One chart series per cause present in the window, ordered by how much of the
// window each accounts for. Built from the response's facets, never from a list
// held here — that is what lets an unknown cause appear without a release.
export const causeHelp = (value) =>
  value == null
    ? 'Spans carrying no cause at all — everything recorded before the device began reporting one.'
    : CAUSE_HELP[value] ||
      `The device reported "${causeLabel(value)}"; this page does not know it.`

export const causeSeries = (facets) =>
  (facets?.seal_cause || []).map(({ value }) => ({
    key: value ?? '__none__',
    cause: value,
    label: causeLabel(value),
    color: causeColor(value),
    help: causeHelp(value),
  }))

// Pull one cause's bucket out of a day row; absent means the cause had no spans
// that day, which is a gap in a line and a zero in a stack.
export const causeBucket = (row, cause) =>
  (row.by_seal_cause || []).find((bucket) => bucket.value === cause)

// Tooltips carry the abandon horizon as a {horizon} token rather than a
// hardcoded number, so the page always states the server's actual setting.
export const fillHorizon = (text, hours) =>
  text.replace(
    /\{horizon\}/g,
    hours == null ? 'abandon' : `${hours % 1 === 0 ? hours : hours.toFixed(1)}h`
  )

const SECOND = 1
const MINUTE = 60
const HOUR = 3600

// Latency here spans milliseconds to hours, so a single unit would render
// most of the range as either 0 or an unreadable pile of digits.
export const fmtSeconds = (seconds) => {
  if (seconds == null) return '—'
  if (seconds === 0) return '0s'
  const abs = Math.abs(seconds)
  if (abs < SECOND) return `${Math.round(seconds * 1000)}ms`
  if (abs < 90 * SECOND)
    return `${abs < 10 ? seconds.toFixed(1) : Math.round(seconds)}s`
  if (abs < 90 * MINUTE) return `${(seconds / MINUTE).toFixed(1)}m`
  return `${(seconds / HOUR).toFixed(1)}h`
}

export const fmtHours = (hours) =>
  hours == null ? '—' : hours >= 10 ? `${Math.round(hours)}h` : `${hours}h`

export const fmtCount = (count) =>
  count == null ? '—' : count.toLocaleString('en-US')

// `durable` counts both seal paths; the chart wants the client's own.
export const durableByClient = (counts) =>
  counts.durable - counts.durable_by_sweep

export const reasonCounts = (counts) => counts.never_durable_by_reason || {}

// The server shares one response across callers for 60s; polling faster than
// that only adds requests that answer with the same bytes.
export const POLL_INTERVAL_MS = 60_000

// The windows the Pipeline and Overview pages read. Span rows carry a 90-day
// TTL, so 90 is the whole of history until the per-user-day rollup exists —
// not a UI choice. `1d` is today so far: the day-bucketed numbers start at
// midnight UTC.
export const WINDOWS = [
  {
    days: 1,
    label: '1d',
    title: 'Today so far (UTC): the day tables start at midnight UTC',
  },
  { days: 7, label: '7d' },
  { days: 30, label: '30d' },
  { days: 90, label: '90d' },
]

// The latency endpoint's day rows summed over the window: the span counts,
// the never-durable reasons and the unsealed hours.
export const windowTotals = (days) =>
  days.reduce(
    (sum, { counts }) => ({
      declared: sum.declared + counts.declared,
      durable: sum.durable + counts.durable,
      durable_by_client: sum.durable_by_client + durableByClient(counts),
      durable_by_sweep: sum.durable_by_sweep + counts.durable_by_sweep,
      never_durable: sum.never_durable + counts.never_durable,
      unsealed_hours:
        Math.round((sum.unsealed_hours + counts.unsealed_hours) * 100) / 100,
      ...REASONS.reduce((acc, { key }) => {
        acc[key] = (sum[key] || 0) + (reasonCounts(counts)[key] || 0)
        return acc
      }, {}),
    }),
    {
      declared: 0,
      durable: 0,
      durable_by_client: 0,
      durable_by_sweep: 0,
      never_durable: 0,
      unsealed_hours: 0,
    }
  )

// Charts read left-to-right in time; the endpoint answers newest first.
export const chronological = (days) => [...days].reverse()

// ---------------------------------------------------------------------------
// Capture health — the half the transport metric is blind to.
//
// Source of truth is `api/services/stream/metrics/capture.py` in reclip-api; keys here
// are its keys. Everything above measures audio that ARRIVED. A phone
// recording 46 minutes of an hour seals every span and appears in none of it.

// Coverage is audio over the wall time the device claimed to be recording,
// folded per account and reported as a distribution: a fleet ratio of sums is
// not a coverage and exceeds 1 as soon as two people record at once.
export const COVERAGE = [
  {
    key: 'p50',
    label: 'Coverage p50',
    color: '#59a14f',
    help: 'The typical account. Near 100% means it recorded as long as it said.',
  },
  {
    key: 'p10',
    label: 'Coverage p10',
    color: '#e15759',
    help: 'The worst tenth of accounts: where a broken phone shows.',
  },
]

// How a phone's recording stopped, by the reason its last heartbeat gave
// (reclip-api `metrics/capture.py`, `_stops`): an open vocabulary, so a reason
// not named here reads as itself. `dirty`, a death, is counted apart. The
// legacy app's words, then reclip-08's: the span's seal cause (`GapCause`) for
// its own stops and for the verdict on the previous run it sends when next
// opened, plus `will_terminate` and `logout`, which both apps send.
export const STOP_WORDS = {
  user_mute: 'turned off',
  logout: 'logged out',
  interruption: 'interrupted',
  will_terminate: 'quit the app',
  none: 'stopped',
  // reclip-08 builds before 2026-10-30 sent the microphone's state, not the
  // span's word; leases still carry them until the phone's next stop.
  off: 'turned off',
  paused: 'paused',
  interrupted: 'interrupted',
  parked: 'interrupted',
  user_stop: 'turned off',
  user_pause: 'paused',
  stalled: 'mic stalled',
  permission_revoked: 'mic permission off',
  other_audio: 'another app took the mic',
  clip_playback: 'played a clip',
  force_quit: 'quit the app',
  system_kill: 'killed by iOS',
  crash: 'crashed',
  device_restart: 'phone restarted',
  app_upgrade: 'app updated',
}

export const stopWords = (reason) =>
  STOP_WORDS[reason] || reason.replace(/_/g, ' ')

// The device's own claim: declared audio over wall time. Coverage is this
// times the fraction that arrived, so the pair decomposes a hole — continuity
// below 1 is a microphone that stopped, coverage below continuity is bytes that
// never landed. One number could not say which, and when there was only one it
// was the declared ratio, which reported an abandoned span as fully covered.
// From reclip-08's D77 (2026-10-28) a hole inside a span is also a hold: the
// phone stops writing while a quote or another app's audio plays and keeps the
// span open, and nothing on the span says which holes were holds yet.
export const CONTINUITY = [
  {
    key: 'p50',
    label: 'Continuity p50',
    color: '#4e79a7',
    help: 'What the phone says it recorded, over the same time. Below 100%: the mic stopped mid-span, or on reclip-08, recording paused while a quote or another app played.',
  },
]

export const CAPTURE_TILES = {
  accounts: 'Accounts that streamed that day.',
  window_accounts:
    'Accounts that streamed on any day in the window, each counted once.',
  coverage:
    'How much of the recording time we actually hold, for the typical account. A span that uploaded nothing counts as zero.',
  continuity:
    'What the phone says it recorded, over the same time. Coverage below this is audio that never arrived.',
  below_bar: 'Accounts whose coverage is under the bar that day.',
  gap_hours:
    'Time not recorded inside spans. The gap between two spans doesn’t count. On reclip-08 this includes pauses while a quote or another app played, under 2 min each.',
  audio_hours:
    'Audio we hold, against what the phones declared. The difference is the loss.',
  kbps: 'The typical account’s bitrate. Drifting from the expected rate hurts transcription before anything breaks.',
  short_rate:
    'Segments under half the expected length, not counting each span’s last. On reclip-08 a pause for a quote or another app ends a segment early, so this reads higher.',
  silent:
    'Phones with the mic on that have sent nothing for a while: the mic is running and the upload isn’t.',
}

// A ratio in [0, 1] as a percentage. Two decimals near the top of the range,
// because the difference between 99.8% and 97% coverage is hours of audio.
export const fmtCoverage = (value) =>
  value == null ? '—' : `${(value * 100).toFixed(value > 0.95 ? 2 : 1)}%`

export const fmtRate = (value) =>
  value == null ? '—' : `${(value * 100).toFixed(1)}%`

export const fmtKbps = (value) => (value == null ? '—' : `${value.toFixed(1)}k`)

// How far the median bitrate sits from what the client pinned. Returned as a
// tone rather than a boolean: the page colors a number it cannot explain, and
// a drift that is merely notable should not read as an outage.
export const kbpsTone = (value, expected) => {
  if (value == null || !expected) return undefined
  const ratio = value / expected
  if (ratio < 0.6 || ratio > 1.6) return 'error.main'
  if (ratio < 0.85 || ratio > 1.15) return 'warning.main'
  return undefined
}

export const coverageTone = (value, bar) => {
  if (value == null || bar == null) return undefined
  return value < bar ? 'error.main' : undefined
}

// ---------------------------------------------------------------------------
// Routing — what the router heard (step 2a).
//
// Source of truth is `api/services/stream/metrics/pipeline.py` and
// `api/services/stream/metrics/routing.py` in reclip-api. A segment is
// `route` (sent to transcription), `skip` (kept, not transcribed — never
// "not stored"), or `expired` (gone before the router saw it). `ok: false`
// is a sub-stage that failed and a segment routed by the broken-gate rule:
// a broken gate must cost money, never audio.

export const DECISIONS = {
  route: {
    label: 'routed',
    color: '#59a14f',
    help: 'Sent to transcription: Silero VAD or YAMNet heard speech.',
  },
  skip: {
    label: 'skipped',
    color: '#8a8f98',
    help: 'Kept, not transcribed: no speech heard. Never deleted.',
  },
  expired: {
    label: 'expired',
    color: '#bab0ac',
    help: 'The audio was gone before the router ran. Nothing was measured.',
  },
  broken: {
    label: 'routed (gate broke)',
    color: '#f28e2c',
    help: 'A check failed, so the minute was sent anyway: a broken check costs money, never audio.',
  },
}

export const decisionOf = (segment) =>
  segment.expired
    ? DECISIONS.expired
    : segment.ok === false
    ? DECISIONS.broken
    : DECISIONS[segment.decision] || DECISIONS.skip

// A fraction in [0, 1] as a percentage with one decimal — speech fractions
// live around 9%, where whole percents lose the signal.
export const fmtPct = (value) =>
  value == null ? '—' : `${(value * 100).toFixed(1)}%`

export const fmtMinutes = (ms) => {
  if (ms == null) return '—'
  // YAMNet's frame is 0.48 s, so a class heard once is under a second and must
  // not round to "0s" — that reads as absent when it means one frame.
  if (ms < 10_000) return `${(ms / 1000).toFixed(1)}s`
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`
  const minutes = ms / 60_000
  return minutes >= 10 ? `${Math.round(minutes)}m` : `${minutes.toFixed(1)}m`
}
